<template>
  <section class="task-sku-switcher" aria-label="订单 SKU 切换">
    <div class="order-summary">
      <span>订单号</span>
      <strong>{{ orderId || '未关联订单' }}</strong>
    </div>

    <div v-if="skus.length" class="sku-select-wrap">
      <label class="sku-select-label" for="task-sku-select">选择 SKU</label>
      <el-select
        id="task-sku-select"
        :model-value="String(activeSkuId || skus[0]?.id || '')"
        class="task-sku-select"
        popper-class="task-sku-select-popper"
        placeholder="选择 SKU"
        :disabled="switching"
        aria-label="选择 SKU"
        @update:model-value="onSkuSelectChange"
      >
        <el-option
          v-for="(sku, index) in skus"
          :key="sku.id"
          :value="String(sku.id)"
          :label="formatSkuOptionLabel(sku, index)"
        >
          <div class="sku-option-row">
            <span class="sku-option-index">SKU{{ index + 1 }}</span>
            <span class="sku-option-name">{{ formatSkuOptionName(sku) }}</span>
          </div>
        </el-option>
      </el-select>
    </div>
  </section>
</template>

<script setup>
import { watch } from 'vue';

const props = defineProps({
  orderId: { type: [String, Number], default: '' },
  skus: { type: Array, default: () => [] },
  activeSkuId: { type: [String, Number], default: '' },
  confirmed: { type: Boolean, default: false },
  switching: { type: Boolean, default: false },
});
const emit = defineEmits(['confirm']);

function formatSkuOptionName(sku) {
  return String(sku.name || sku.productCategory || sku.specification || '未命名').trim();
}

function formatSkuOptionLabel(sku, index) {
  const title = formatSkuOptionName(sku);
  return title ? `SKU${index + 1} · ${title}` : `SKU${index + 1}`;
}

function findSku(value) {
  return props.skus.find(item => String(item.id) === String(value)) || null;
}

function onSkuSelectChange(value) {
  const sku = findSku(value);
  if (!sku || props.switching) return;
  emit('confirm', sku);
}

watch(
  () => [props.activeSkuId, props.confirmed, props.skus],
  () => {
    if (props.confirmed || props.switching) return;
    const sku = findSku(props.activeSkuId) || props.skus[0];
    if (sku) emit('confirm', sku);
  },
  { immediate: true, deep: true },
);

defineExpose({ selectSkuById: onSkuSelectChange });
</script>

<style scoped>
.task-sku-switcher {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, .06);
  font-family: Inter, "PingFang SC", sans-serif;
}
.order-summary {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  flex: none;
}
.order-summary span {
  flex: none;
  color: #94a3b8;
  font-size: 11px;
}
.order-summary strong {
  max-width: 220px;
  overflow: hidden;
  color: #1f2937;
  font-size: 13px;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sku-select-wrap {
  flex: 1 1 240px;
  min-width: 180px;
  max-width: 320px;
}
.sku-select-label {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.task-sku-select {
  width: 100%;
}
.task-sku-select :deep(.el-select__wrapper) {
  min-height: 34px;
  border-radius: 10px;
  box-shadow: 0 0 0 1px #e5e7eb inset;
  background: #fff;
  padding-left: 12px;
  padding-right: 12px;
  transition: box-shadow .15s ease, background-color .15s ease;
}
.task-sku-select :deep(.el-select__wrapper:hover) {
  box-shadow: 0 0 0 1px #cbd5e1 inset;
  background: #f8fafc;
}
.task-sku-select :deep(.el-select__wrapper.is-focused),
.task-sku-select :deep(.el-select__wrapper.is-hovering) {
  box-shadow: 0 0 0 1px #9ae6b4 inset;
  background: #f5f3ff;
}
.task-sku-select :deep(.el-select__selected-item) {
  color: #0f172a;
  font-size: 12px;
  font-weight: 650;
}
.task-sku-select :deep(.el-select__placeholder) {
  color: #94a3b8;
  font-size: 12px;
}
.sku-option-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.sku-option-index {
  flex: none;
  padding: 2px 8px;
  border-radius: 999px;
  background: #ecfdf3;
  color: #07883c;
  font-size: 11px;
  font-weight: 700;
}
.sku-option-name {
  overflow: hidden;
  color: #303133;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@media (max-width: 900px) {
  .task-sku-switcher {
    flex-wrap: wrap;
    gap: 8px;
  }
  .sku-select-wrap {
    flex-basis: 100%;
    max-width: none;
  }
}
</style>

<style>
.task-sku-select-popper.el-popper {
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 16px 40px rgba(15, 23, 42, .12);
}
.task-sku-select-popper .el-select-dropdown__item {
  height: auto;
  min-height: 36px;
  padding: 8px 12px;
  line-height: 1.4;
}
.task-sku-select-popper .el-select-dropdown__item.is-selected {
  background: #ecfdf3;
  color: #07883c;
  font-weight: 600;
}
.task-sku-select-popper .el-select-dropdown__item.is-selected .sku-option-name {
  color: #07883c;
}
</style>
